/* eslint-disable react/no-array-index-key */

import { withStyles } from '@material-ui/core/styles'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import cn from 'classnames'

import Flag from '../Flag'

const styles = theme => ({
  tabRoot: {
    padding: '20px 30px',
    minWidth: '100px',
    color: `var(--gameday-tabs-tabInActiveColor, ${theme.tabs.tabInActiveColor})`,
    fontSize: theme.typography.fontSizeSm,
    fontWeight: theme.typography.fontWeightBolder,
    letterSpacing: 'normal',
    textTransform: 'none',
    opacity: 1,
    '&.icon-position-start .custom-icon': {
      marginRight: 10,
      marginTop: -5,
    },
    '&.icon-position-end .custom-icon': {
      order: 1,
      marginLeft: 10,
      marginTop: -5,
    },
  },
  selected: {
    background: `var(--gameday-tabs-tabActiveBackgroundColor, ${theme.tabs.tabActiveBackgroundColor})`,
    color: `var(--gameday-tabs-tabActiveColor, ${theme.tabs.tabActiveColor})`,
    borderTop: '0px',
    '& .tab-num': {
      backgroundColor: `var(--gameday-tabs-tabActiveColor, ${theme.tabs.tabActiveColor})`,
      color: `var(--gameday-tabs-tabNavigatorNumberColor, ${theme.tabs.tabNavigatorNumberColor})`,
      borderColor: `var(--gameday-tabs-tabActiveColor, ${theme.tabs.tabActiveColor})`,
    },
  },
  indicator: {
    bottom: '0',
    backgroundColor: 'transparent',
    height: '0',
  },
  scrollButtons: {
    '&.MuiButtonBase-root': {
      background: `var(--gameday-tabs-tabActiveBackgroundColor, ${theme.tabs.tabActiveBackgroundColor})`,
      color: theme.colors.charcoal,
    },
    '&:empty': {
      display: 'none',
    },
  },
  labelIcon: {
    minHeight: '48px',
    '& .MuiTab-wrapper > *:first-child': {
      marginBottom: '0',
    },
  },
  labelWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    lineHeight: '1.2em',
  },
  flag: {
    flexDirection: 'row-reverse',
    lineHeight: '1.2em',
    alignItems: 'center',
  },
  tabNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    marginRight: '5px',
    border: `1px solid ${`var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})`}`,
    borderRadius: '10px',
    fontSize: theme.typography.fontSizeTinyish,
  },
  subTabsRoot: {
    minHeight: '36px',
    '& $indicator': {
      display: 'none',
    },
    '& $tabRoot': {
      fontWeight: 'normal',
      border: `1px solid ${`var(--gameday-borders-borderColor, ${theme.borders.borderColor})`}`,
      minHeight: '36px',
      padding: '0 16px',
      color: theme.textColor,
      opacity: '1',
      backgroundColor: `var(--gameday-tabs-tabBackgroundColor, ${theme.tabs.tabBackgroundColor})`,
      '&.hasError + $tabRoot': {
        borderLeftColor: theme.status.errorBorder,
      },
      '&.hasError': {
        color: theme.status.errorColor,
        backgroundColor: `var(--gameday-tabs-tabBackgroundErrorColor, ${theme.tabs.tabBackgroundErrorColor})`,
        borderColor: theme.status.errorBorder,
      },
      '&$selected': {
        fontWeight: theme.typography.fontWeightBolder,
        backgroundColor: `var(--gameday-tabs-tabActiveBackgroundColor, ${theme.tabs.tabActiveBackgroundColor})`,
        '&.hasError + $tabRoot': {
          borderLeftColor: theme.status.errorBorder,
        },
        '&.hasError': {
          color: theme.status.errorColor,
          backgroundColor: `var(--gameday-tabs-tabBackgroundErrorColor, ${theme.tabs.tabBackgroundErrorColor})`,
          borderColor: theme.status.errorBorder,
        },
      },
      '&:not(:first-child)': {
        marginLeft: '-1px',
      },
      '&:first-child': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
      },
      '&:last-child': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
      },
      '&:not($selected):hover': {
        backgroundColor: `var(--gameday-tabs-tabBackgroundColor, ${theme.tabs.tabBackgroundColor})`,
      },
    },
    '& > .MuiTabs-scrollButtons:first-child': {
      borderRight: theme.borders.default,
    },
    '& > .MuiTabs-scrollButtons:last-child': {
      borderLeft: theme.borders.default,
    },
    '& > .MuiTabs-scroller': {
      '-ms-overflow-style': 'none',
    },
  },
  mainTabsRoot: {
    '& > .MuiTabs-scroller': {
      '-ms-overflow-style': 'none',
    },
    '& $tabRoot': {
      backgroundColor: `var(--gameday-tabs-tabBackgroundColor, ${theme.tabs.tabBackgroundColor})`,
      '&:first-child': {
        borderTopLeftRadius: '12px',
      },
      '&:last-child': {
        borderTopRightRadius: '12px',
      },
      '&:not($selected):hover': {
        backgroundColor: `var(--gameday-tabs-tabBackgroundHoverColor, ${theme.tabs.tabBackgroundHoverColor})`,
      },
    },
    '& $selected': {
      background: `var(--gameday-tabs-tabActiveBackgroundColor, ${theme.tabs.tabActiveBackgroundColor})`,
    },
  },
})

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  }
}

const TabNavigatorNew = ({
  activeTab,
  classes,
  mainTabs,
  onChangeTab,
  scrollButtons,
  subTabs,
  tabs,
  variant,
  tabErrors = [],
  withNumbers,
}) => {
  if (activeTab === -1) {
    // Return the tab as empty as there are no available tabs visible.
    // biome-ignore lint/complexity/noUselessFragments: TODO
    return <></>
  }

  // Check if activeTab is visible
  const currentTab = tabs.find(x => x.value === activeTab) || {}
  const skip = currentTab.show === false
  if (skip) {
    const index = tabs.findIndex(x => x.value !== activeTab && x.show !== false)
    if (index === -1) {
      // No page found with show tab === true. Set the activeTab's index to be -1
      onChangeTab(index)
    } else {
      const setStateImmediately = true
      // This is required IF onChangeTab does not set the status immediately (e.g. via setTimeout) and will loop endlessly.
      // Pass setStateImmediately and instantly fire setState with new tab value to avoid the loop.
      onChangeTab(tabs[index].value, setStateImmediately)
    }
  }

  return (
    <Tabs
      value={activeTab}
      onChange={onChangeTab}
      variant={variant}
      scrollButtons={scrollButtons}
      classes={{
        root: !subTabs ? classes.mainTabsRoot : classes.subTabsRoot,
        indicator: classes.indicator,
        scrollButtons: classes.scrollButtons,
      }}
      className={cn({ subTabs, mainTabs })}
    >
      {tabs.map((tab, key) => {
        if (!tab.show) {
          return null
        }

        let tabIcon = null
        let tabIconPosition = 'end'

        if (withNumbers) {
          tabIcon = <span className={cn(classes.tabNumber, 'tab-num')}>{tab.value + 1}</span>
          tabIconPosition = 'start'
        } else if (tab.flag) {
          tabIcon = tab.toolTip ? (
            <Flag
              variant={tab.flag}
              title={tab.flag}
              showToolTip
              toolTipText={tab.toolTip}
              style={{ marginLeft: '10px' }}
            />
          ) : (
            <Flag variant={tab.flag} title={tab.flag} style={{ marginLeft: '10px' }} />
          )
        } else if (tab.icon) {
          tabIcon = tab.icon
          tabIconPosition = tab.iconPosition || 'end'
        }

        return (
          <Tab
            label={tab.title}
            value={tab.value}
            className={cn(tabErrors.includes(tab.value) && 'hasError', `icon-position-${tabIconPosition}`)}
            icon={tabIcon}
            // biome-ignore lint/suspicious/noArrayIndexKey: TODO
            key={key}
            onClick={() => onChangeTab(tab.value)}
            aria-label={tab.value}
            data-testid={`${tab.title}MenuTab`}
            classes={{
              root: classes.tabRoot,
              selected: classes.selected,
              wrapper: classes.labelWrapper,
              labelIcon: classes.labelIcon,
              ...(tab.flag && { wrapper: classes.flag }),
            }}
            {...a11yProps(tab.value)}
          />
        )
      })}
    </Tabs>
  )
}

TabNavigatorNew.defaultProps = {
  activeTab: '',
  onChangeTab: () => {},
  mainTabs: false,
  scrollButtons: 'on',
  subTabs: false,
  tabs: [],
  variant: 'scrollable',
  withNumbers: false,
  tabErrors: [],
}

const TabComponent = withStyles(styles)(TabNavigatorNew)
TabComponent.displayName = 'TabNavigatorNew'
export default TabComponent
